import React from 'react';
import { IconButtonTypes } from '@lib/components/IconButton/IconButton';
import { NavItem } from '@lib/components/Menu/Menu';
import { PaginationProps } from '@lib/hooks/usePagination';
import { OrderingState, PaginationState } from '@lib/enums/urls';
import { TooltipProps } from '@lib/components/Tooltip/Tooltip';
import { UseBulkSelectResult } from '@lib/hooks/useTableItemsBulkSelect';

export interface BaseItem extends Record<string, unknown> {
  id: number | string;
}

export interface TableTreeRowState {
  depth: number;
  parentId: string | null;
  hasChildren: boolean;
  isExpanded: boolean;
  isLoadingChildren: boolean;
  /** When present, the row is a flat search result and this replaces the tree affordance. */
  path?: React.ReactNode;
}

export interface TableTreeProps<T extends BaseItem> {
  getRowState: (item: T) => TableTreeRowState;
  onToggleRow: (item: T) => void;
  rootCount: number;
  /** Rows at or beyond this depth (0-indexed) get their select checkbox disabled. */
  maxDepth?: number;
  /** Tooltip body shown on the select checkbox once maxDepth is reached. */
  maxDepthTooltip?: React.ReactNode;
  /**
   * True when search/filter has collapsed the hierarchy into a flat match
   * list (rows carry `path` instead of expand affordances) — the currently
   * rendered rows are then the complete result set, so "select all" is
   * meaningful again and the header checkbox should show.
   */
  isFlatView?: boolean;
}

export interface TableAction<T extends BaseItem> {
  icon: string;
  id: string;
  onClick: (item: T) => void;
  predictorFnDisable?: (item: T) => boolean;
  predictorFnDisableTooltipProps?: (item: T) => Omit<TooltipProps, 'children'>;
  predictorFnTooltipProps?: (item: T) => Omit<TooltipProps, 'children'>;
  predictorFnVisible?: (item: T) => boolean;
  type: IconButtonTypes;
}

export interface CellProps<T extends BaseItem> {
  currentUserId?: string;
  disabled?: boolean;
  item: T;
  onClick?: () => void;
  value?: React.ReactNode;
}

export type Cell<T extends BaseItem> =
  React.FC<CellProps<T>> | ((props: CellProps<T>) => React.ReactElement);

export enum CellContentAlign {
  Start = 'start',
  Center = 'center',
  End = 'end',
}

export type HeaderTooltipInfo = {
  tooltipBody: string;
  icon: string;
  symbolsOutlined?: boolean;
};

export interface CellContentProps<T extends BaseItem> {
  column: Column<T>;
  value?: string;
  item: T;
}

export interface Column<T extends BaseItem> {
  Cell?: Cell<T>;
  Footer?: React.FC<{ data: T[] }>;
  Header: React.ReactNode;
  accessor?: string;
  accessorFn?: (props: CellContentProps<T>) => React.ReactNode;
  actions?: TableAction<T>[];
  actionsMenu?: NavItem<T>[];
  bordered?: boolean;
  cellClassName?: string;
  contentAlign?: CellContentAlign;
  headerTooltipInfo?: HeaderTooltipInfo;
  hideHeader?: boolean;
  id?: string | number;
  isCustomHeader?: boolean;
  isSingleLineHeader?: boolean;
  maxWidth?: number;
  minWidth?: number;
  noWrap?: boolean;
  prohibitDisabled?: boolean;
  sortable?: boolean;
  width?: number;
}

type OnSortChangeFn = (
  newOrderingField?: string,
  newOrderingDirection?: string,
) => void;

type TableComponents = {
  TopHeadComponent: React.ElementType;
};

export interface TableProps<T extends BaseItem> extends PaginationProps {
  actions?: TableAction<T>[];
  actionsForSelected?: React.ReactNode[];
  actionsMenu?: NavItem<T>[];
  bordered?: boolean;
  borderedCells?: boolean;
  className?: string;
  columns: Column<T>[];
  components?: TableComponents;
  counterText?: string;
  data: T[];
  emptyState?: React.ReactNode;
  Footer?: React.FC;
  fullHeight?: boolean;
  getIsRowDisabled?: (item: T) => boolean;
  getIsRowSelected?: (item: T) => boolean;
  getRowClassName?: (v: T) => string | undefined;
  hasSelectColumn?: boolean;
  selectionLimit?: number;
  id?: string | number;
  loading?: boolean;
  onRowClick?: (item: T) => void;
  onSortChange?: OnSortChangeFn;
  orderingDirection?: string;
  orderingDirectionKey?: string;
  orderingField?: string;
  orderingFieldKey?: string;
  orderingState?: OrderingState;
  paginationState?: PaginationState;
  /**
   * Only meaningful with `selectionLimit={1}`: renders a real `RadioButton`
   * per row (no header "select all") and checking a row swaps the selection
   * straight to it instead of disabling every other row until the current
   * pick is unchecked first. Opt-in so existing `selectionLimit={1}` callers
   * (e.g. plan-capped attachment pickers) keep their current Checkbox-based
   * block-until-unchecked behavior unless they ask for the swap.
   */
  radioSelect?: boolean;
  rounded?: boolean;
  tableContainerClassName?: string;
  tree?: TableTreeProps<T>;
  useBulkSelectResult?: UseBulkSelectResult<T>;
}
